import "./slider.css"
import type { ChangeEvent } from "react"
import { useEffect, useRef, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "./button"

// Native range slider styled after the Sidebar "alternative" story: a compact
// row of label · flexible track · right-aligned value readout. Optional
// chevron buttons keep fine-stepping for callers that pass `navigation`.
// `debounced` defers onValueChange until the change stream settles for 500ms,
// so a drag doesn't thrash the caller — the thumb and readout still track the
// drag live through a local mirror of the value.
export function Slider(input: {
  debounced?: boolean
  label?: string
  max: number
  min: number
  navigation?: { step: number }
  onValueChange: (value: number)=> void
  step: number
  value: number
}) {
  // A debounced drag leaves the parent on the last settled value, so the
  // thumb/readout track a local mirror while changes stream in.
  const [liveValue, setLiveValue] = useState(input.value)
  useEffect(()=> {
    setLiveValue(input.value)
  }, [input.value])

  const displayedValue = input.debounced ? liveValue : input.value

  // WebKit paints the whole rail as one element, so the filled portion needs a
  // gradient stop computed from the current value (Firefox has ::-moz-range-progress).
  const progressPercent =
    ((Math.min(Math.max(displayedValue, input.min), input.max) - input.min) /
      (input.max - input.min || 1)) *
    100

  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(()=> {
    return ()=> clearTimeout(timerRef.current)
  }, [])

  // Immediate for callers without `debounced`; settled (500ms) otherwise.
  const commitValue = (nextValue: number)=> {
    if (!input.debounced) {
      input.onValueChange(nextValue)
      return
    }
    setLiveValue(nextValue)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(()=> {
      input.onValueChange(nextValue)
    }, 500)
  }
  const onChange = (event: ChangeEvent<HTMLInputElement>)=> {
    commitValue(Number(event.target.value))
  }
  const onPrev = ()=> {
    commitValue(Math.max(input.min, displayedValue - input.navigation?.step))
  }
  const onNext = ()=> {
    commitValue(Math.min(input.max, displayedValue + input.navigation?.step))
  }
  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-center gap-2 py-1 text-xs">
        {input.label && (
          <span className="w-20 text-foreground">{input.label}</span>
        )}
        <input
          aria-label={input.label}
          className="min-w-0 flex-1 range-slider focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none"
          max={input.max}
          min={input.min}
          onChange={onChange}
          step={input.step}
          style={{ "--slider-progress": `${progressPercent}%` } as React.CSSProperties}
          type="range"
          value={displayedValue}
        />
        <span className="w-8 text-right tabular-nums text-muted-foreground">
          {displayedValue}
        </span>
      </label>
      {input.navigation && (
        <div className="flex justify-center gap-2">
          <Button
            disabled={displayedValue <= input.min}
            onClick={onPrev}
            size="icon"
            variant="outline"
          >
            <ChevronLeft />
          </Button>
          <Button
            disabled={displayedValue >= input.max}
            onClick={onNext}
            size="icon"
            variant="outline"
          >
            <ChevronRight />
          </Button>
        </div>
      )}
    </div>
  )
}